import { type Ref, ref } from 'vue'

export type ApiFailure = { status: number; message: string }

export type ScopedApiRequest<T> = {
  result: Promise<T | undefined>
  loading: Ref<boolean>
  failure: Ref<ApiFailure | undefined>
}

type Result = { data: unknown; error: unknown; response?: unknown }

// Treaty answers a request that never landed with a 503 and no response at all, so there is no server status to report.
const UNREACHABLE: ApiFailure = { status: 503, message: 'the API could not be reached' }

const failureOf = (error: unknown): ApiFailure => {
  const { status, value } = (error ?? {}) as { status?: unknown; value?: unknown }
  const code = typeof status === 'number' ? status : 0
  const message =
    typeof value === 'string'
      ? value
      : typeof (value as { error?: unknown } | null)?.error === 'string'
        ? (value as { error: string }).error
        : `request failed with status ${code}`
  return { status: code, message }
}

const runOn = async <T>(
  loading: Ref<boolean>,
  failure: Ref<ApiFailure | undefined>,
  call: () => Promise<Result>
): Promise<T | undefined> => {
  loading.value = true
  failure.value = undefined
  try {
    const { data, error, response } = await call()
    if (error) {
      failure.value = response ? failureOf(error) : UNREACHABLE
      return undefined
    }
    return (data ?? undefined) as T | undefined
  } catch {
    failure.value = UNREACHABLE
    return undefined
  } finally {
    loading.value = false
  }
}

export const useApiRequest = () => {
  const loading = ref(false)
  const failure = ref<ApiFailure | undefined>()

  const run = <T>(call: () => Promise<Result>) => runOn<T>(loading, failure, call)

  // Its own loading/failure pair, so a call that is not the view's primary one does not
  // clear the notice the primary one just set.
  const runScoped = <T>(call: () => Promise<Result>): ScopedApiRequest<T> => {
    const scopedLoading = ref(false)
    const scopedFailure = ref<ApiFailure | undefined>()
    return { result: runOn<T>(scopedLoading, scopedFailure, call), loading: scopedLoading, failure: scopedFailure }
  }

  return { run, loading, failure, runScoped }
}

// A page's own load: only the latest call writes loading and failure, and a superseded call returns nothing (#245).
export const useApiLoad = () => {
  const loading = ref(false)
  const failure = ref<ApiFailure | undefined>()
  let latest = 0

  const run = async <T>(call: () => Promise<Result>) => {
    const request = ++latest
    const own = ref<ApiFailure | undefined>()
    loading.value = true
    failure.value = undefined
    const data = await runOn<T>(ref(false), own, call)
    if (request !== latest) return undefined
    loading.value = false
    failure.value = own.value
    return data
  }

  const reset = () => {
    latest++
    loading.value = false
    failure.value = undefined
  }

  return { run, reset, loading, failure }
}
